"use client";

import { useLayoutEffect, useState } from "react";

import { useCountUp, type CountUpMode } from "@/hooks/useCountUp";

export type DashStatAnimatedValueProps = {
  raw: number;
  mode: CountUpMode;
  /** True after inventory is ready, desk launch (if any) finished, and a short post-reveal delay. */
  statsReveal: boolean;
  /** Stagger start relative to `statsReveal` (ms). */
  delayMs: number;
  durationMs?: number;
  /** Increments on desk launch replay so counters reset cleanly. */
  replayKey: number;
};

export function DashStatAnimatedValue({
  raw,
  mode,
  statsReveal,
  delayMs,
  durationMs = 1200,
  replayKey,
}: DashStatAnimatedValueProps) {
  const [localStart, setLocalStart] = useState(false);

  useLayoutEffect(() => {
    if (!statsReveal) {
      setLocalStart(false);
      return undefined;
    }
    if (delayMs <= 0) {
      setLocalStart(true);
      return undefined;
    }
    setLocalStart(false);
    const id = window.setTimeout(() => setLocalStart(true), delayMs);
    return () => window.clearTimeout(id);
  }, [statsReveal, delayMs, replayKey]);

  const text = useCountUp(raw, durationMs, {
    start: localStart,
    mode,
    resetKey: `${replayKey}:${statsReveal ? 1 : 0}:${delayMs}`,
  });

  const awaitingStagger = statsReveal && !localStart;

  return (
    <span className={awaitingStagger ? "ed-stat-value--await" : undefined} aria-busy={awaitingStagger || undefined}>
      {text}
    </span>
  );
}
